import React, { useState } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Filter, ChevronDown } from 'lucide-react';
import { ALL_CATEGORY_NAMES } from '../constants/cloudResources';

interface CategoryFilterDropdownProps {
  selectedCategories: Set<string>;
  onCategoryChange: (category: string, checked: boolean) => void;
  onToggleAll: () => void;
  /**
   * 'popover' uses Radix Popover (portals to body — works standalone, breaks inside Dialog).
   * 'inline' uses a plain absolutely-positioned div (works inside Dialog).
   * Default: 'inline'
   */
  variant?: 'popover' | 'inline';
}

export const CategoryFilterDropdown: React.FC<CategoryFilterDropdownProps> = ({
  selectedCategories,
  onCategoryChange,
  onToggleAll,
  variant = 'inline',
}) => {
  const [open, setOpen] = useState(false);

  const label = selectedCategories.size === ALL_CATEGORY_NAMES.length
    ? 'All categories'
    : `${selectedCategories.size} of ${ALL_CATEGORY_NAMES.length}`;

  const toggleLabel = selectedCategories.size === ALL_CATEGORY_NAMES.length
    ? 'Deselect all'
    : 'Select all';

  const content = (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Filter by category</p>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={onToggleAll}
        >
          {toggleLabel}
        </button>
      </div>
      <div className="space-y-2">
        {ALL_CATEGORY_NAMES.map(cat => (
          <label key={cat} className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={selectedCategories.has(cat)}
              onCheckedChange={(checked) => onCategoryChange(cat, !!checked)}
            />
            <span className="text-sm">{cat}</span>
          </label>
        ))}
      </div>
    </div>
  );

  if (variant === 'popover') {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center gap-1.5 whitespace-nowrap">
            <Filter className="h-3.5 w-3.5" />
            <Badge variant="secondary" className="text-xs font-normal">
              {label}
            </Badge>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" align="end">
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  // Inline variant (for use inside Radix Dialog)
  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-1.5 whitespace-nowrap"
        onClick={() => setOpen(!open)}
      >
        <Filter className="h-3.5 w-3.5" />
        <Badge variant="secondary" className="text-xs font-normal">
          {label}
        </Badge>
        <ChevronDown className="h-3 w-3" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 p-3 rounded-md border bg-popover text-popover-foreground shadow-md z-[60]">
          {content}
        </div>
      )}
    </div>
  );
};
